import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export const CampaignDetailDialog = ({ campaignId, open, onOpenChange, onRetried }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get(`/campaigns/${campaignId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !campaignId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId]);

  const retry = async () => {
    setRetrying(true);
    try {
      const res = await api.post(`/campaigns/${campaignId}/retry`);
      toast.success(`Reintentando el envío a ${res.data.retrying} destinatario(s)`);
      onRetried?.();
      setTimeout(load, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reintentar el envío");
    } finally { setRetrying(false); }
  };

  const log = data?.log || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="campaign-detail-dialog" className="max-w-2xl max-sm:max-w-[95vw] max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display pr-6">{data?.subject || "Detalle de la campaña"}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#00AEEF]" /></div>
        ) : !data ? (
          <p className="text-sm text-neutral-500 py-6 text-center">No pudimos cargar el detalle de esta campaña.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-600">{data.recipients_total} destinatario(s)</span>
              <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">{data.sent_count} enviados</span>
              {data.failed_count > 0 && <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-600 font-medium">{data.failed_count} fallidos</span>}
              {data.status === "sending" && <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">Enviando...</span>}
            </div>

            {data.failed_count > 0 && data.status !== "sending" && (
              <Button data-testid="retry-failed-btn" onClick={retry} disabled={retrying}
                variant="outline" className="rounded-xl gap-2 w-full sm:w-auto">
                {retrying ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                Reenviar solo a {data.failed_count} {data.failed_count === 1 ? "fallido" : "fallidos"}
              </Button>
            )}

            {log.length === 0 ? (
              <p className="text-sm text-neutral-400 py-6 text-center">Todavía no hay registros de envío.</p>
            ) : (
              <div className="rounded-xl border border-neutral-200 overflow-hidden" data-testid="campaign-log">
                <table className="w-full text-sm hidden sm:table">
                  <thead className="bg-neutral-50 text-neutral-500 text-left text-xs">
                    <tr>
                      <th className="px-3 py-2 font-medium">Nombre</th>
                      <th className="px-3 py-2 font-medium">Correo</th>
                      <th className="px-3 py-2 font-medium">Estado</th>
                      <th className="px-3 py-2 font-medium">Motivo del fallo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((r, i) => (
                      <tr key={i} className="border-t border-neutral-100" data-testid={`campaign-log-row-${i}`}>
                        <td className="px-3 py-2 font-medium">{r.name}</td>
                        <td className="px-3 py-2 text-neutral-500 break-all">{r.email}</td>
                        <td className="px-3 py-2">
                          {r.status === "sent" ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 size={13} /> Enviado</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><XCircle size={13} /> Fallido</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-neutral-500">{r.error || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Lista apilada en móvil para que no se desborde */}
                <div className="sm:hidden divide-y divide-neutral-100">
                  {log.map((r, i) => (
                    <div key={i} data-testid={`campaign-log-card-${i}`} className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm min-w-0 break-words">{r.name}</p>
                        {r.status === "sent" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold shrink-0"><CheckCircle2 size={13} /> Enviado</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold shrink-0"><XCircle size={13} /> Fallido</span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500 mt-1 break-all">{r.email}</p>
                      {r.error && <p className="text-xs text-red-500 mt-1.5 break-words leading-snug">{r.error}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
